import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Headphones, Play, BookOpen, Grid, ArrowRight, Award, BookMarked, Sparkles, ChevronDown, Gift, Heart, Users, HelpCircle, TrendingUp, Mountain, Trophy } from 'lucide-react';
import CookieConsent from './CookieConsent';
import PWAInstallPrompt from './PWAInstallPrompt';
import GrowthBuddy from './GrowthBuddy';
import EditableText from './EditableText';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BOOKING_URL = "/katies-plan";

// Track CTA clicks for analytics
const trackEvent = async (eventName, eventData = {}) => {
  try {
    await fetch(`${API_URL}/api/admin/track-event?event_name=${eventName}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData)
    });
  } catch (e) {
    // Silent fail - don't block user
  }
};

// Sticky mobile Book a Call button (appears after scrolling on long pages)
const StickyBookButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <a
      href={`${BOOKING_URL}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent('book_call_click', { source: 'sticky_mobile' })}
      className={`md:hidden fixed bottom-4 left-4 right-24 z-40 inline-flex items-center justify-center gap-2 bg-[#E91E8C] text-white px-5 py-3.5 rounded-full font-bold text-sm shadow-2xl shadow-[#E91E8C]/40 transition-all duration-300 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-24 opacity-0 pointer-events-none'
      }`}
      data-testid="sticky-book-call-btn"
      aria-hidden={!visible}
    >
      <Phone className="w-4 h-4" />
      Apply for Katie's Plan
    </a>
  );
};

// Navigation items - FREE Resources dropdown, Katie's Book, Programs
const navItems = [
  { 
    path: '#', 
    label: 'FREE Resources', 
    icon: Gift, 
    dropdown: true,
    items: [
      { path: '/start-here', label: 'Start Here', description: 'New? Begin your journey here', icon: Sparkles },
      { path: '/listen', label: 'Listen', description: 'Treat Your Business Podcast', icon: Headphones },
      { path: '/watch', label: 'Watch', description: 'YouTube videos & training', icon: Play },
      { path: '/learn', label: 'Learn', description: 'Articles & insights', icon: BookOpen },
      { path: '/topics', label: 'Topics', description: 'Browse by category', icon: Grid },
      { path: '/client-success', label: 'Success Stories', description: 'Real clinic owner results', icon: Trophy },
    ]
  },
  { path: 'https://offers.thrive-businesscoaching.com/thrivingbook', label: "Katie's Book", icon: BookMarked, external: true },
  { 
    path: '#', 
    label: 'Programs', 
    icon: Award, 
    dropdown: true,
    items: [
      { path: '/katies-plan', label: "Katie's Plan", description: 'Katie personally looks at your clinic - free', icon: Sparkles },
      { path: '/elevate', label: 'Elevate', description: 'Scale with structure', icon: TrendingUp },
      { path: '/ascend', label: 'Ascend', description: 'Step into the visionary role', icon: Mountain },
    ]
  },
  { 
    path: '#', 
    label: 'About', 
    icon: Heart, 
    dropdown: true,
    items: [
      { path: '/about-katie-bell', label: 'About Us', description: 'Led by clinic owners, built for clinic owners', icon: Heart },
      { path: '/partners-sponsors', label: 'Partners & Sponsors', description: 'The brands who support our podcast', icon: Users },
      { path: '/faq-library', label: 'FAQs', description: 'Your questions, answered', icon: HelpCircle },
    ]
  },
];

// Marquee CTA Banner Component
const MarqueeCTA = () => {
  return (
    <div className="bg-gradient-to-r from-[#E91E8C] to-[#d11a7d] text-white py-2 overflow-hidden">
      <div className="marquee-container">
        <div className="marquee-content inline-flex items-center gap-8 whitespace-nowrap">
          {[...Array(6)].map((_, i) => (
            <span key={i} className="inline-flex items-center gap-8" aria-hidden={i > 0 ? "true" : undefined}>
              <a
                href="/katies-plan"
                className="inline-flex items-center gap-2 text-sm font-medium"
                data-testid={`marquee-katies-plan-link-${i}`}
                tabIndex={i > 0 ? -1 : 0}
                onClick={() => trackEvent('katies_plan_click', { source: 'marquee_banner' })}
              >
                <span className="text-yellow-300">★</span>
                <EditableText as="span" sectionId="global-header" field="marquee-text-kp" defaultValue="Apply for Katie's Plan for Your Clinic - two slots a week" />
                <ArrowRight className="w-4 h-4" />
              </a>
              <span className="mx-4">|</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const buildBookingUrl = (source = 'website', medium = 'referral', content = 'nav') => {
    return BOOKING_URL;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Rolling CTA Marquee Banner */}
      <MarqueeCTA />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/thrive-logo.png" 
                alt="Thrive - Treat Your Business" 
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = !item.external && !item.dropdown && (location.pathname === item.path || 
                  (item.path !== '/' && location.pathname.startsWith(item.path)));
                
                // Handle dropdown menus
                if (item.dropdown) {
                  const dropdownTestId = item.label.toLowerCase().replace(/\s+/g, '-');
                  return (
                    <div key={item.label} className="relative group">
                      <button
                        className="flex items-center gap-1 px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide transition-colors text-gray-700 hover:text-[#E91E8C] hover:bg-gray-50"
                        data-testid={`nav-${dropdownTestId}`}
                      >
                        <EditableText as="span" sectionId="global-nav" field={`label-${dropdownTestId}`} defaultValue={item.label} />
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        <div className="py-2">
                          {item.items.map((subItem) => {
                            const SubIcon = subItem.icon;
                            return subItem.external ? (
                              <a
                                key={subItem.path}
                                href={subItem.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                                onClick={() => trackEvent('nav_click', { source: 'nav', item: subItem.label })}
                              >
                                {SubIcon && <SubIcon className="w-5 h-5 text-[#E91E8C] mt-0.5 flex-shrink-0" />}
                                <div>
                                  <EditableText as="span" sectionId="global-nav" field={`sub-label-${subItem.path}`} defaultValue={subItem.label} className="block text-sm font-semibold text-gray-900" />
                                  <EditableText as="span" sectionId="global-nav" field={`sub-desc-${subItem.path}`} defaultValue={subItem.description} className="block text-xs text-gray-500" />
                                </div>
                              </a>
                            ) : (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                                onClick={() => trackEvent('nav_click', { source: 'nav', item: subItem.label })}
                              >
                                {SubIcon && <SubIcon className="w-5 h-5 text-[#E91E8C] mt-0.5 flex-shrink-0" />}
                                <div>
                                  <EditableText as="span" sectionId="global-nav" field={`sub-label-${subItem.path}`} defaultValue={subItem.label} className="block text-sm font-semibold text-gray-900" />
                                  <EditableText as="span" sectionId="global-nav" field={`sub-desc-${subItem.path}`} defaultValue={subItem.description} className="block text-xs text-gray-500" />
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }
                
                if (item.external) {
                  const handleExternalClick = () => {
                    if (item.label === "Katie's Book") {
                      trackEvent('katies_book_click', { source: 'nav' });
                    } else if (item.label === "Programs") {
                      trackEvent('programs_click', { source: 'nav' });
                    }
                  };
                  
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide transition-colors text-gray-700 hover:text-[#E91E8C] hover:bg-gray-50"
                      data-testid={`nav-${item.label.toLowerCase().replace(/'/g, '')}`}
                      onClick={handleExternalClick}
                    >
                      {item.label}
                    </a>
                  );
                }
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide transition-colors ${
                      isActive
                        ? 'text-[#E91E8C]'
                        : 'text-gray-700 hover:text-[#E91E8C] hover:bg-gray-50'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href={buildBookingUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#E91E8C] hover:bg-[#d11a7d] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors book-call-cta"
                data-testid="nav-book-call"
                onClick={() => trackEvent('book_call_click', { source: 'nav_button' })}
              >
                <Phone className="w-4 h-4" />
                <EditableText as="span" sectionId="global-header" field="cta-label" defaultValue="Apply for Katie's Plan" />
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white">
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = !item.external && !item.dropdown && location.pathname === item.path;
                
                // Handle dropdown items in mobile - show as expanded section
                if (item.dropdown) {
                  return (
                    <div key={item.label} className="space-y-1">
                      <div className="flex items-center gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                        <Icon className="w-4 h-4" />
                        {item.label}
                      </div>
                      <div className="pl-4 space-y-1">
                        {item.items.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = location.pathname === subItem.path;
                          return subItem.external ? (
                            <a
                              key={subItem.path}
                              href={subItem.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
                            >
                              {SubIcon && <SubIcon className="w-4 h-4 text-[#E91E8C]" />}
                              {subItem.label}
                            </a>
                          ) : (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium ${
                                isSubActive
                                  ? 'bg-pink-50 text-[#E91E8C]'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {SubIcon && <SubIcon className="w-4 h-4 text-[#E91E8C]" />}
                              {subItem.label}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                
                if (item.external) {
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-gray-600 hover:bg-gray-50"
                    >
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </a>
                  );
                }
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium ${
                      isActive
                        ? 'bg-gray-100 text-[#E91E8C]'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
              <a
                href={buildBookingUrl('website', 'referral', 'mobile-nav')}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#E91E8C] text-white px-4 py-3 rounded-lg font-semibold mt-4"
              >
                <Phone className="w-5 h-5" />
                Apply for Katie's Plan
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Growth Buddy Chatbot */}
      <GrowthBuddy />

      {/* Sticky mobile Book a Call button */}
      <StickyBookButton />

      <SiteFooter />
    </div>
  );
};

export const SiteFooter = ({ curveClass = 'bg-white' }) => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterDone, setNewsletterDone] = useState(false);
  const [newsletterError, setNewsletterError] = useState('');

  return (
    <>
      {/* Simple Curve Before Footer */}
      <div className={curveClass}>
        <svg className="block w-full" viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ height: '60px' }}>
          <path 
            fill="#1B2B4B"
            d="M0,60 L0,20 Q360,60 720,30 Q1080,0 1440,30 L1440,60 Z"
          />
        </svg>
      </div>

      {/* Footer */}
      <footer className="bg-[#1B2B4B] text-white -mt-[1px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Compact Footer Layout */}
          <div className="flex flex-col lg:flex-row justify-between gap-8">
            {/* Brand */}
            <div className="lg:max-w-xs">
              <Link to="/" className="flex items-center gap-2 mb-3">
                <img 
                  src="/thrive-logo.png" 
                  alt="Thrive - Treat Your Business" 
                  className="h-8 w-auto brightness-0 invert"
                />
              </Link>
              <EditableText as="p" sectionId="global-footer" field="description" multiline defaultValue="Helping UK clinic owners build thriving practices through business coaching, marketing systems, and leadership development." className="text-gray-400 text-sm leading-relaxed" />
              <a
                href="mailto:enquiries@thrive-businesscoaching.com"
                className="inline-flex items-center gap-2 mt-3 text-gray-400 hover:text-[#E91E8C] text-sm transition-colors"
                data-testid="footer-contact-email"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                enquiries@thrive-businesscoaching.com
              </a>
              {/* Newsletter signup */}
              <div className="mt-5">
                <EditableText as="p" sectionId="global-footer" field="newsletter-heading" defaultValue="Get clinic growth tips in your inbox" className="text-white text-sm font-semibold mb-2" />
                {newsletterDone ? (
                  <p className="text-[#E91E8C] text-sm font-medium" data-testid="newsletter-success">✓ You're subscribed - welcome aboard!</p>
                ) : (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!newsletterEmail.trim()) return;
                      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(newsletterEmail.trim())) {
                        setNewsletterError("That doesn't look like a full email address - please double-check it");
                        return;
                      }
                      setNewsletterError('');
                      try {
                        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email: newsletterEmail })
                        });
                        if (r.ok) setNewsletterDone(true);
                        else if (r.status === 400) setNewsletterError("That doesn't look like a full email address - please double-check it");
                      } catch (err) { /* silent */ }
                    }}
                    className="flex gap-2"
                  >
                    <input
                      type="email"
                      required
                      value={newsletterEmail}
                      onChange={(e) => { setNewsletterEmail(e.target.value); if (newsletterError) setNewsletterError(''); }}
                      placeholder="Your email address"
                      className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#E91E8C]"
                      data-testid="newsletter-email-input"
                    />
                    <button
                      type="submit"
                      className="bg-[#E91E8C] hover:bg-[#d11a7d] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex-shrink-0"
                      data-testid="newsletter-submit-btn"
                    >
                      Join
                    </button>
                  </form>
                )}
                {newsletterError && !newsletterDone && (
                  <p className="text-xs text-red-400 mt-1" data-testid="newsletter-email-error">{newsletterError}</p>
                )}
              </div>
            </div>

            {/* Links Grid - Compact */}
            <div className="flex flex-wrap gap-x-12 gap-y-6">
              {/* Content */}
              <div>
                <EditableText as="h3" sectionId="global-footer" field="col-content" defaultValue="Content" className="font-semibold text-xs uppercase tracking-wider mb-3 text-gray-500" />
                <ul className="space-y-1.5">
                  <li><Link to="/listen" className="text-gray-400 hover:text-white text-sm transition-colors">Podcast</Link></li>
                  <li><Link to="/watch" className="text-gray-400 hover:text-white text-sm transition-colors">Videos</Link></li>
                  <li><Link to="/learn" className="text-gray-400 hover:text-white text-sm transition-colors">Blog</Link></li>
                  <li><Link to="/topics" className="text-gray-400 hover:text-white text-sm transition-colors">Topics</Link></li>
                </ul>
              </div>
              
              {/* Programs */}
              <div>
                <EditableText as="h3" sectionId="global-footer" field="col-programs" defaultValue="Programs" className="font-semibold text-xs uppercase tracking-wider mb-3 text-gray-500" />
                <ul className="space-y-1.5">
                  <li><Link to="/katies-plan" className="text-[#E91E8C] hover:text-white text-sm font-medium transition-colors" data-testid="footer-katies-plan-link">Katie's Plan</Link></li>
                  <li><Link to="/elevate" className="text-gray-400 hover:text-white text-sm transition-colors">Elevate</Link></li>
                  <li><Link to="/ascend" className="text-gray-400 hover:text-white text-sm transition-colors">Ascend</Link></li>
                </ul>
              </div>

              {/* Company */}
              <div>
                <EditableText as="h3" sectionId="global-footer" field="col-company" defaultValue="Company" className="font-semibold text-xs uppercase tracking-wider mb-3 text-gray-500" />
                <ul className="space-y-1.5">
                  <li><Link to="/about-katie-bell" className="text-gray-400 hover:text-white text-sm transition-colors">About Katie</Link></li>
                  <li><Link to="/partners-sponsors" className="text-gray-400 hover:text-white text-sm transition-colors">Partners</Link></li>
                  <li><Link to="/faq-library" className="text-gray-400 hover:text-white text-sm transition-colors">FAQ</Link></li>
                </ul>
              </div>

              {/* Legal */}
              <div>
                <EditableText as="h3" sectionId="global-footer" field="col-legal" defaultValue="Legal" className="font-semibold text-xs uppercase tracking-wider mb-3 text-gray-500" />
                <ul className="space-y-1.5">
                  <li><Link to="/privacy-policy" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy</Link></li>
                  <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">Terms</Link></li>
                  <li><Link to="/cookie-policy" className="text-gray-400 hover:text-white text-sm transition-colors">Cookies</Link></li>
                </ul>
              </div>
            </div>

            {/* Social + CTA */}
            <div className="flex flex-col items-start lg:items-end gap-4">
              <div className="flex gap-2">
                <a 
                  href="https://www.instagram.com/thrivebusinesscoaching/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 hover:bg-[#E91E8C] rounded-lg flex items-center justify-center transition-colors"
                  aria-label="Instagram"
                  onClick={() => trackEvent('social_click', { platform: 'instagram', source: 'footer' })}
                >
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a 
                  href="https://www.facebook.com/thrivebusinesscoaching" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 hover:bg-[#E91E8C] rounded-lg flex items-center justify-center transition-colors"
                  aria-label="Facebook"
                  onClick={() => trackEvent('social_click', { platform: 'facebook', source: 'footer' })}
                >
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a 
                  href="https://www.youtube.com/@thrivebizcoach" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 hover:bg-[#E91E8C] rounded-lg flex items-center justify-center transition-colors"
                  aria-label="YouTube"
                  onClick={() => trackEvent('social_click', { platform: 'youtube', source: 'footer' })}
                >
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
                <a 
                  href="https://www.linkedin.com/company/thrive-business-coaching/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 hover:bg-[#E91E8C] rounded-lg flex items-center justify-center transition-colors"
                  aria-label="LinkedIn"
                  onClick={() => trackEvent('social_click', { platform: 'linkedin', source: 'footer' })}
                >
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              </div>
              <a
                href={BOOKING_URL}
                                className="inline-flex items-center gap-2 bg-[#E91E8C] hover:bg-[#d11a7d] text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors shadow-[0_0_20px_rgba(233,30,140,0.3)]"
                onClick={() => trackEvent('book_call_click', { source: 'footer' })}
              >
                <Phone className="w-4 h-4" />
                Apply for Katie's Plan
              </a>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-6 text-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Thrive Business Coaching. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Layout;
